import React, { useContext, useId, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import Button, { ButtonProps } from '@amzn/awsui-components-core/button';
import { CustomIcon, getCustomIconSvg, CustomIconName } from '../CustomIcon/CustomIcon';
import { VideoModal } from '../VideoModal/VideoModal';
import { DownloadIcon, PlayCircleIcon } from '../../utils/inlineIcons';
import { getButtonStyle, getFoucStyle } from './CtaButton.styles';
import { ctaTargetAttributes, joinDescribedBy, CtaBehavior, CTA_NEW_TAB_DESCRIPTION } from '../../utils/ctaBehavior';
import { inlineMarkdownRehypePlugins } from '../../utils/inlineMarkdown';
import type { CtaAction } from '../../utils/ctaAction';
import { dispatchPopupEvent, normalizePopupId } from '../../utils/popupEvents';
import { CtaTrackingPrefixContext, useClickTracking } from '../../hooks/useClickTracking';
import './CtaButton.scss';

/**
 * CtaButton - Lightweight Cloudscape Button wrapper for CTA blocks
 *
 * Extends Cloudscape Button with Poly-specific convenience features:
 * - `text` prop as CMS-friendly alias for children
 * - `customIconName` for custom icons
 * - `colorVariant` for per-instance color theming via Cloudscape Style API
 * - All other Cloudscape Button props pass through unchanged
 *
 * Color and padding styles are applied through Cloudscape's Style API
 * (not CSS classes) because Cloudscape renders <a> when href is present,
 * and CSS class overrides are not a supported customization mechanism.
 * See: https://core.cloudscape.aws.dev/foundation/visual-foundation/customizing-the-foundation/styling/
 */

export const CTA_COLOR_VARIANTS = ['primary', 'contrast', 'secondary', 'tertiary', 'offWhite'] as const;
export type CtaButtonColorVariant = (typeof CTA_COLOR_VARIANTS)[number];

export const CTA_SIZES = ['default', 'medium', 'small', 'nav', 'compact'] as const;
export type CtaButtonSize = (typeof CTA_SIZES)[number];

/**
 * @polySemanticUsage Placeholder for now
 */
export interface CtaButtonProps extends Omit<ButtonProps, 'variant'> {
  /** CMS-friendly text prop as alternative to children */
  text?: string;

  /** Custom icon name from CustomIcon component */
  customIconName?: CustomIconName;

  /** Color variant — see {@link CtaButtonColorVariant} */
  colorVariant?: CtaButtonColorVariant;

  /** Size variant (default: large 64px, medium: 48px, small and compact: 40px) */
  size?: CtaButtonSize;

  /** ID attribute for the button element */
  id?: string;

  /** data-ld-append attribute for analytics tracking */
  ldAppendix?: string;

  /** Small text displayed below the button (e.g., pricing info); supports inline markdown such as links */
  subtext?: string;

  /**
   * Whether the link opens in the current tab or a new one; new tab adds `rel="noopener noreferrer"`
   * and a screen-reader-only "Opens in a new tab" description.
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Behavior
   * @polyTranslatable false
   */
  behavior?: CtaBehavior;

  /**
   * What the button does when activated (default `link`):
   * - `link`: navigates to `href`
   * - `video`: adds a play icon after the text and opens `href` in a video modal instead of navigating
   *   (YouTube links play embedded; other URLs play in a native player)
   * - `download`: adds a download icon before the text and an arrow after it, and sets the `download`
   *   attribute. Browsers ignore `download` for files on another origin, which then open in the browser instead.
   * - `popup`: opens the Popup block whose Popup ID matches `popupTargetId`; `href` and `behavior` are ignored
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID and does not use the URL or Behavior fields.
   * @polyDefault link
   * @polyLabel Action
   * @polyTranslatable false
   */
  action?: CtaAction;

  /**
   * Popup ID to open when Action is Popup.
   * @polyLabel Target Popup ID
   * @polyHelp When Action is Popup, match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive. The Popup must be available on the rendered page.
   * @polyTranslatable false
   */
  popupTargetId?: string;
}

export const CtaButton: React.FC<CtaButtonProps> = ({
  text,
  customIconName,
  colorVariant = 'primary',
  size = 'default',
  id,
  ldAppendix,
  subtext,
  behavior,
  action = 'link',
  popupTargetId,
  children,
  iconSvg,
  ariaDescribedby,
  href,
  nativeButtonAttributes,
  ...buttonProps
}) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const resolvedIconSvg = customIconName ? getCustomIconSvg(customIconName) : iconSvg;
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const subtextId = subtext ? `${baseId}-subtext` : undefined;
  const opensPopup = action === 'popup';
  const newTabDescriptionId = behavior === 'newTab' && !opensPopup ? `${baseId}-new-tab` : undefined;
  const hasIcon = !!(customIconName || iconSvg);
  const label = children || text;
  const opensVideo = action === 'video' && !!href;
  const resolvedHref = opensPopup ? undefined : href;

  // Callers such as MegaMenu and MobileDrawer author the identifier as `data-cta-id` rather than
  // `id`, so it is the tracking fallback: otherwise one authored nav CTA reports a derived slug in
  // the header bar and its authored id in the drawer. The label falls back to string `children`
  // because Hero, PreFooter and SuperCard pass the label that way rather than through `text`.
  const authoredCtaId = id ?? (buttonProps as { 'data-cta-id'?: string })['data-cta-id'];
  const trackingLabel = opensPopup
    ? normalizePopupId(popupTargetId)
    : text ?? (typeof children === 'string' ? children : undefined);
  const trackingPrefix = useContext(CtaTrackingPrefixContext);
  const trackClick = useClickTracking(trackingPrefix, {
    ctaId: authoredCtaId,
    href: resolvedHref,
    label: trackingLabel,
  });
  const popupTriggerRef = useRef<HTMLElement | null>(null);

  // Tracking and caller handlers run for every action before the component handles the action.
  const handleClick: ButtonProps['onClick'] = (event) => {
    trackClick();
    buttonProps.onClick?.(event);
    if (event.defaultPrevented) return;
    if (opensPopup) {
      event.preventDefault();
      if (popupTriggerRef.current) {
        dispatchPopupEvent(popupTriggerRef.current, popupTargetId ?? '');
      }
    } else if (opensVideo) {
      event.preventDefault();
      setIsVideoOpen(true);
    }
  };

  const mergedNativeButtonAttributes = opensPopup ? {
    ...nativeButtonAttributes,
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      nativeButtonAttributes?.onClick?.(event);
      if (!event.defaultPrevented) popupTriggerRef.current = event.currentTarget;
    },
  } : nativeButtonAttributes;

  return (
    <div className={`poly-cta-button poly-cta-button--${size} poly-cta-button--${colorVariant}${subtext ? ' poly-cta-button--has-subtext' : ''}`} style={getFoucStyle(colorVariant)}>
      <Button
        {...buttonProps}
        href={resolvedHref}
        {...ctaTargetAttributes(opensPopup ? undefined : behavior)}
        onClick={handleClick}
        nativeButtonAttributes={mergedNativeButtonAttributes}
        formAction={opensPopup ? 'none' : buttonProps.formAction}
        download={action === 'download' ? true : buttonProps.download}
        variant="primary"
        iconSvg={resolvedIconSvg}
        style={getButtonStyle({ colorVariant, size, hasIcon, hasTrailingIcon: action === 'video' && !!label })}
        id={id}
        data-ld-append={ldAppendix}
        ariaDescribedby={joinDescribedBy(ariaDescribedby, subtextId, newTabDescriptionId)}
      >
        {label ? (
          <span className="poly-cta-button__text">
            {action === 'link' || action === 'popup' ? label : (
              <span className="poly-cta-button__action-content">
                {action === 'download' && <DownloadIcon className="poly-cta-button__action-icon" />}
                {label}
                {action === 'video' && <PlayCircleIcon className="poly-cta-button__action-icon" />}
                {action === 'download' && (
                  <CustomIcon
                    name="arrow-forward"
                    size="var(--poly-size-ctaButton-actionIcon)"
                    className="poly-cta-button__action-icon"
                    aria-hidden="true"
                  />
                )}
              </span>
            )}
          </span>
        ) : null}
      </Button>
      {subtext && (
        <>
          {/* aria-describedby is flattened to a text string and should reference plain text, so the
              description target is a hidden text-only copy; the visible subtext keeps its links operable. */}
          <span id={subtextId} className="poly-visually-hidden" aria-hidden="true">
            <ReactMarkdown rehypePlugins={inlineMarkdownRehypePlugins} allowedElements={[]} unwrapDisallowed>
              {subtext}
            </ReactMarkdown>
          </span>
          <div className="poly-cta-button__subtext">
            <ReactMarkdown rehypePlugins={inlineMarkdownRehypePlugins}>{subtext}</ReactMarkdown>
          </div>
        </>
      )}
      {newTabDescriptionId && <span id={newTabDescriptionId} className="poly-visually-hidden">{CTA_NEW_TAB_DESCRIPTION}</span>}
      {isVideoOpen && href && (
        <VideoModal src={href} onClose={() => setIsVideoOpen(false)} />
      )}
    </div>
  );
};
